import LoadingErrorWrapper from '../shared_components/loadingErrorWrapper';
import React, { useState, useEffect, Suspense, useCallback, useRef } from 'react';
import {
  Skeleton,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  AlertTitle,
  Typography,
  Snackbar,
  IconButton,
} from '@mui/material';
import {

  Explore,
  TrendingUp,
  MapsHomeWork,
  Assessment,
  DonutSmallOutlined,
  AutoGraphOutlined,
  PersonPinCircleOutlined,
  Edit as EditIcon,

  Cancel as CancelIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';

// Shared Components
import PageLayout from '../shared_components/pageLayout';
import AccordionSection from '../shared_components/accordionSection';
import AccordionContainer from '../shared_components/accordionContainer';
import InitialSchoolSelector from '../shared_components/initialSchoolSelector';
import ProjectionsFilters from '../shared_components/projectionsFilters';
import NowWhatSection from '../shared_components/templates/nowWhatTemplate';
import SplitLayout from '../shared_components/splitLayout';
import AISummaryAccordion from '../shared_components/AISummaryAccordion';

// Other Components
import EnrollmentManagementModal from './EnrollmentManagementModal';
import GradeCapacityManager from './GradeCapacityManager';

// Custom Hooks
import { useAccordionState } from '../../hooks/useAccordionState';
import { useFeatureUsage, PRODUCT_AREAS } from '../../hooks/useFeatureUsage';
import { useInsight } from '../../contexts/InsightContext';

// Utils
import { formatSchoolName } from '../../utils/nameFormatter';
import { schoolsApi, clearApiCache } from '../../utils/apiService';
import { filterEnrollmentDataForFutureOnly } from '../../utils/projectionsFilters';
import { isDropAPinSchool, countEnrollmentYears, MIN_YEARS_FOR_PROJECTIONS } from '../../utils/schoolDataDetection';
import { regenerateProjectionsSummary } from '../../services/aiService';


// Contexts
import { useAuth } from '../../auth/AuthContext';

// Cache
import { useTeamSchoolCache } from '../../hooks/teamCacheService';

// Stores
import useSchoolStore from '../../stores/schoolStore';

// Constants
const GRADES = ['Kindergarten', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const AVAILABLE_SCHOOL_YEARS = ['2024-2025', '2025-2026'];  // Full format matches database storage convention

// Lazy loaded components
const ProjectXDashboard = React.lazy(() => import('./dashboard/ProjectXDashboard'));
const ProjectXTargetsAssessment = React.lazy(() => import('./ProjectXTargetsAssessment'));
const ProjectXStrategyImpact = React.lazy(() => import('./ProjectXStrategyImpact'));
const DropAPinProjectionsView = React.lazy(() => import('./DropAPinProjectionsView'));
const HybridProjectionsView = React.lazy(() => import('./HybridProjectionsView'));

function ProjectionsExplorer() {
  const trackUsage = useFeatureUsage(PRODUCT_AREAS.PROJECTIONS_EXPLORER);

  // Get insight context
  const { setToolAndSection } = useInsight();

  // Get data from team school cache
  const { 
    schools, 
    loading: schoolsLoading, 
  } = useTeamSchoolCache();

  // Use global school selection from store (shared across all explorers)
  const { selectedSchool } = useSchoolStore();
  
  // Local state management (ProjectionsExplorer uses different data than other explorers)
  const [contentLoading, setContentLoading] = useState(false);

  // Drop-a-pin locations have no enrollment history, so they get the market-context
  // worksheet (DropAPinProjectionsView) instead of enrollment projections.
  const isDropAPin = isDropAPinSchool(selectedSchool);

  // Schools with 1-4 years of enrollment get the hybrid view (history + planning worksheet)
  // unless the user opts into the standard projections view.
  const [enrollmentHistory, setEnrollmentHistory] = useState(null);
  const [enrollmentYearCount, setEnrollmentYearCount] = useState(null);
  const [yearCountLoading, setYearCountLoading] = useState(false);
  const [forceStandardView, setForceStandardView] = useState(false);
  const activeSchoolUuidRef = useRef(null);
  const forceStandardViewRef = useRef(false);
  forceStandardViewRef.current = forceStandardView;
  const isShortHistory =
    enrollmentYearCount != null && enrollmentYearCount > 0 && enrollmentYearCount < MIN_YEARS_FOR_PROJECTIONS;
  const showHybrid = !isDropAPin && isShortHistory && !forceStandardView;
  
  // State management
  const [enrollmentData, setEnrollmentData] = useState(null);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('5-year');
  const [view, setView] = useState('school-level');
  const [selectedGrades, setSelectedGrades] = useState(['total']);
  const [availableGrades, setAvailableGrades] = useState(['total']);
  const [openDataDialog, setOpenDataDialog] = useState(false);
  const [missingDataTypes, setMissingDataTypes] = useState({ enrollment: false, capacity: false });

  const [hasSkippedEnrollmentEntry, setHasSkippedEnrollmentEntry] = useState(false);
  const [adjustedProjections, setAdjustedProjections] = useState(null);
  const [enrollmentTargets, setEnrollmentTargets] = useState({});
  const [selectedScenarios, setSelectedScenarios] = useState(['low', 'median', 'high']);
  const [showSuccess, setShowSuccess] = useState(false);
  const [aiUpdateNotification, setAiUpdateNotification] = useState({ open: false, message: '', severity: 'info' });
  const [aiSummaryRefreshKey, setAiSummaryRefreshKey] = useState(0);
  
  // Track which schools are currently having AI summaries regenerated
  // Using a ref so it persists across school changes
  const regeneratingSchoolsRef = useRef(new Set());
  const [regeneratingAI, setRegeneratingAI] = useState(false); // Tracks if CURRENT school is regenerating
  
  const initialSchoolLoadRef = useRef(false);
  const hasShownDialogRef = useRef(false);
  const [sectionLoadingStates, setSectionLoadingStates] = useState({
    risk: { loading: true, dataReady: false },
    impact: { loading: true, dataReady: false },
    actions: { loading: false, dataReady: true } // "Now What?" is always ready
  });

  // Enrollment management modal state
  const [enrollmentModalOpen, setEnrollmentModalOpen] = useState(false);
  const [projectionsLoading, setProjectionsLoading] = useState(false);

  // Grade capacity modal state
  const [capacityModalOpen, setCapacityModalOpen] = useState(false);

  // Use accordion state hook
  const {
    expandedSections,
    handleExpandedChange,
    handleExpandAll,
    handleCollapseAll,
  } = useAccordionState({
    risk: false,
    impact: false,
    actions: false,
  });

  const isSectionDataLoaded = useCallback((sectionId) => {
    switch (sectionId) {
      case 'risk':
        return !!enrollmentData && !!selectedSchool && !projectionsLoading;
      case 'impact':
        return !!enrollmentData && !!selectedSchool && !projectionsLoading;
      case 'actions':
        return true; // Always ready
      default: 
        return false;
    }
  }, [enrollmentData, selectedSchool, projectionsLoading]);

  // Initial mount effect - sets default insights
  useEffect(() => {
    setToolAndSection('projections-explorer', null);
  }, [setToolAndSection]);  

  // Handle URL parameters for direct navigation from other explorers (initial load)
  useEffect(() => {
    if (schools.length > 0 && !selectedSchool && !initialSchoolLoadRef.current) {
      const params = new URLSearchParams(window.location.search);
      const schoolId = params.get('school');
      
      if (schoolId) {
        // Support both UUID and NCES ID for compatibility with other explorers
        const school = schools.find(s => s.uuid === schoolId || s.ncessch === schoolId);
        if (school) {
          useSchoolStore.getState().selectSchool({
            ...school,
            name: formatSchoolName(school.name)
          });
        }
      }
      initialSchoolLoadRef.current = true;
    }
  }, [schools, selectedSchool]);
  
  // Watch for URL changes even after a school is already selected
  // This ensures navigation from other tools (like AI Summary) updates the selected school
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const schoolIdFromUrl = params.get('school');
    
    // If URL has a school parameter and it's different from the currently selected school
    if (schoolIdFromUrl && schools.length > 0 && selectedSchool?.uuid !== schoolIdFromUrl) {
      const school = schools.find(s => s.uuid === schoolIdFromUrl || s.ncessch === schoolIdFromUrl);
      if (school) {
        useSchoolStore.getState().selectSchool({
          ...school,
          name: formatSchoolName(school.name)
        });
      }
    }
  }, [window.location.search, schools, selectedSchool?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Reset the initialSchoolLoadRef when the component unmounts
  useEffect(() => {
    return () => {
      initialSchoolLoadRef.current = false;
    };
  }, []);

  // Monitor expanded sections and update insights accordingly
  useEffect(() => {
    const hasExpandedSection = Object.values(expandedSections).some(isExpanded => isExpanded);
    if (!hasExpandedSection) {
      setToolAndSection('projections-explorer', null);
    } else {
      // Set the insight section based on which section is expanded
      const expandedSection = Object.keys(expandedSections).find(key => expandedSections[key]);
      if (expandedSection) {
        setToolAndSection('projections-explorer', expandedSection);
      }
    }
  }, [expandedSections, setToolAndSection]);

  // Get user from AuthContext
  const { user } = useAuth();
  
  // Helper function to check if user can edit data
  const canEditData = useCallback(() => {
    const editRoles = ['admin', 'updated_write'];
    return editRoles.includes(user?.role);
  }, [user?.role]);


  // Helper function to check if school has enrollment data for the most recent available year
  const checkHasRecentEnrollmentData = useCallback(async (schoolUuid) => {
    try {
      const managementData = await schoolsApi.getEnrollmentManagementData(schoolUuid);
      const enrollmentData = managementData?.enrollment_data || {};
      
      // Get the most recent school year from available years (sorted descending)
      const sortedYears = AVAILABLE_SCHOOL_YEARS.sort((a, b) => {
        const yearA = parseInt(a.split('-')[0]);
        const yearB = parseInt(b.split('-')[0]);
        return yearB - yearA; // Most recent first
      });
      
      const mostRecentYear = sortedYears[0];
      
      // Check if most recent year data exists and has actual enrollment numbers
      const hasRecentData = !!(
        enrollmentData[mostRecentYear] && 
        Object.keys(enrollmentData[mostRecentYear]).length > 0 &&
        Object.values(enrollmentData[mostRecentYear]).some(gradeData => 
          gradeData && gradeData.total && gradeData.total > 0
        )
      );
      
      return hasRecentData;
    } catch (error) {
      console.error('Error checking recent enrollment data:', error);
      return false; // Default to showing dialog if we can't check
    }
  }, []);

  // Helper function to check if school has grade capacity data
  const checkHasCapacityData = useCallback(async (schoolUuid) => {
    try {
      const capacityData = await schoolsApi.getGradeCapacities(schoolUuid);
      
      // Check if capacity data exists and has actual capacity values
      const hasCapacityData = !!(
        capacityData?.capacities && 
        Object.keys(capacityData.capacities).length > 0
      );
      
      return hasCapacityData;
    } catch (error) {
      console.error('Error checking capacity data:', error);
      return false; // Default to showing dialog if we can't check
    }
  }, []);

  // Returns the year count, or null if it couldn't be determined (falls back to the standard view)
  const loadEnrollmentHistory = useCallback(async (schoolUuid) => {
    setYearCountLoading(true);
    try {
      const data = await schoolsApi.getEnrollmentManagementData(schoolUuid, true);
      if (activeSchoolUuidRef.current !== schoolUuid) return null;
      const count = countEnrollmentYears(data?.enrollment_data);
      setEnrollmentHistory(data);
      setEnrollmentYearCount(count);
      return count;
    } catch (error) {
      console.error('Error loading enrollment history:', error);
      if (activeSchoolUuidRef.current === schoolUuid) {
        setEnrollmentHistory(null);
        setEnrollmentYearCount(null);
      }
      return null;
    } finally {
      if (activeSchoolUuidRef.current === schoolUuid) setYearCountLoading(false);
    }
  }, []);

  const fetchProjections = useCallback(async () => {
    if (!selectedSchool?.uuid) return;
    
    setContentLoading(true);
    setProjectionsLoading(true);
    
    // Set component loading states
    setSectionLoadingStates({
      risk: { loading: true, dataReady: false },
      impact: { loading: true, dataReady: false },
      actions: { loading: false, dataReady: true }
    });
    
    setAdjustedProjections(null);
    setEnrollmentTargets({});
    
    try {
      // Use V2 API for enrollment projections with UUID
      const data = await schoolsApi.getEnrollmentProjectionsV2(selectedSchool.uuid);
      
      // Filter projections to only show future years (exclude years with actual enrollment data)
      // This prevents backtest/historical projections from appearing in the production explorer
      const filteredData = filterEnrollmentDataForFutureOnly(data);
      
      setEnrollmentData(filteredData);
      
      // Update component loading states
      setSectionLoadingStates(prev => ({
        ...prev,
        risk: { loading: false, dataReady: true },
        impact: { loading: false, dataReady: true }
      }));
      
      setProjectionsLoading(false);
      setContentLoading(false);
  
      // Process grades from the most recent historical enrollment data
      const grades = new Set();
      
      if (data.actual_enrollment) {
        // Get the most recent year with enrollment data
        const enrollmentYears = Object.keys(data.actual_enrollment || {}).sort();
        const latestYear = enrollmentYears.pop();
        
        if (latestYear && data.actual_enrollment[latestYear]) {
          // Extract all grades from the latest enrollment year
          Object.entries(data.actual_enrollment[latestYear]).forEach(([grade, enrollment]) => {
            // Only include grades with valid enrollment data (>= 0)
            if (enrollment !== undefined && enrollment >= 0) {
              let normalizedGrade;
              if (grade === '0' || grade === 'K' || grade === 'Kindergarten') {
                normalizedGrade = 'Kindergarten';
              } else if (!['PK', 'Pre-K', 'PreK', 'Pre-Kindergarten'].includes(grade)) {
                // Handle both "Grade X" and plain number formats
                normalizedGrade = grade.replace('Grade ', '');
              }
              if (normalizedGrade) {
                grades.add(normalizedGrade);
              }
            }
          });
        }
      }
      
      // Fallback to projections data if no historical data is available
      if (grades.size === 0 && data.projections && data.projections.median) {
        const projectionYears = Object.keys(data.projections.median);
        if (projectionYears.length > 0) {
          const firstYear = projectionYears[0];
          const yearData = data.projections.median[firstYear];
          Object.keys(yearData).forEach(grade => {
            const normalizedGrade = grade.replace('Grade ', '');
            if (normalizedGrade !== 'total') {
              grades.add(normalizedGrade);
            }
          });
        }
      }
  
      const sortedGrades = GRADES.filter(grade => grades.has(grade));
      setAvailableGrades(sortedGrades);
    
      // Only show dialog if:
      // 1. User has edit permissions
      // 2. School doesn't have recent enrollment data or capacity data
      // 3. We haven't shown the dialog yet
      // 4. Schools are loaded (not checking contentLoading since we just finished loading)
      if (canEditData() && 
          !hasShownDialogRef.current && 
          !schoolsLoading) {
        
        // Check if recent enrollment data and capacity data exist
        const hasRecentEnrollmentData = await checkHasRecentEnrollmentData(selectedSchool.uuid);
        const hasCapacityData = await checkHasCapacityData(selectedSchool.uuid);
        
        // Show dialog if either enrollment or capacity data is missing
        if (!hasRecentEnrollmentData || !hasCapacityData) {
          setMissingDataTypes({
            enrollment: !hasRecentEnrollmentData,
            capacity: !hasCapacityData
          });
          setOpenDataDialog(true);
        }
        hasShownDialogRef.current = true;
      }
  
    } catch (error) {
      // Only log non-422 errors since 422 errors are expected for locations without enrollment data
      if (error.status !== 422) {
        console.error('Error fetching projections:', error);
      }
      
      // Check if this is a 422 error about missing enrollment data
      if (error.status === 422) {
        // Check if this is a drop-a-pin location (no NCES ID)
        const isDropAPinError = !selectedSchool.ncessch;
        
        if (isDropAPinError) {
          setError('DROP_A_PIN_LOCATION');
        } else {
          setError('NO_ENROLLMENT_DATA');
        }
      } else if (error.message && error.message.includes('location without enrollment data')) {
        setError('LOCATION_ONLY');
      } else {
        setError('Failed to fetch projections: ' + (error.message || 'Unknown error'));
      }
      
      // Update loading states even on error
      setSectionLoadingStates(prev => ({
        ...prev,
        risk: { loading: false, dataReady: false },
        impact: { loading: false, dataReady: false }
      }));
      
      setProjectionsLoading(false);
      setContentLoading(false);
    }
  }, [selectedSchool?.uuid, selectedSchool?.ncessch, canEditData, checkHasRecentEnrollmentData, checkHasCapacityData, schoolsLoading]);

  // No enrollment data message component
  const NoEnrollmentDataMessage = () => (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        py: 8,
        px: 4,
        textAlign: 'center',
        minHeight: 400
      }}
    >
      <Box
        component="img"
        src="/images/computer_projections.png"
        alt="No Enrollment Data Available"
        sx={{
          width: '100%',
          maxWidth: 400,
          height: 'auto',
          mb: 3,
          opacity: 0.8
        }}
      />
      <Typography variant="h5" color="text.primary" gutterBottom sx={{ fontWeight: 600 }}>
        No Enrollment Data Available
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600 }}>
        This school currently does not have sufficient enrollment data for projection calculations. Enrollment projections require historical enrollment data to generate accurate forecasts.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Button 
          variant="contained" 
          onClick={() => {
            useSchoolStore.getState().clearSchoolData();
            setError(null);
          }}
          startIcon={<Assessment />}
          size="large"
        >
          Select Different School
        </Button>
        <Button 
          variant="outlined" 
          href={`/geographic-explorer?school=${selectedSchool?.uuid}`}
          startIcon={<MapsHomeWork />}
          size="large"
        >
          Explore Demographics
        </Button>
      </Box>
    </Box>
  );

  // Location-only message component
  const LocationOnlyMessage = () => (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        py: 8,
        px: 4,
        textAlign: 'center',
        minHeight: 400
      }}
    >
      <Box
        component="img"
        src="/images/future_projections.png"
        alt="Projections Not Available"
        sx={{
          width: '100%',
          maxWidth: 400,
          height: 'auto',
          mb: 3,
          opacity: 0.7
        }}
      />
      <Typography variant="h5" color="text.primary" gutterBottom sx={{ fontWeight: 600 }}>
        Projections Not Available
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500 }}>
        Enrollment projections require historical enrollment data. This location represents a geographic point for market analysis but does not have enrollment data for projection calculations.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Button 
          variant="outlined" 
          href={`/geographic-explorer?school=${selectedSchool?.uuid}`}
          startIcon={<MapsHomeWork />}
          size="large"
        >
          Explore Demographics
        </Button>
        <Button 
          variant="outlined" 
          href={`/competitive-explorer?school=${selectedSchool?.uuid}`}
          startIcon={<DonutSmallOutlined />}
          size="large"
        >
          Analyze Market Share
        </Button>
      </Box>
    </Box>
  );

  useEffect(() => {
    if (!enrollmentData) return;
    
    const sortedGrades = availableGrades.filter(grade => grade !== 'total');
    if (view === 'grade-level' && sortedGrades.length > 0) {
      if (selectedGrades.length === 0 || selectedGrades[0] === 'total') {
        setSelectedGrades([sortedGrades[0]]);
      }
    } else if (view === 'school-level' && !selectedGrades.includes('total')) {
      setSelectedGrades(['total']);
    }
  }, [view, enrollmentData, availableGrades, selectedGrades]);

  // Fetch projections when school changes (from any explorer)
  useEffect(() => {
    if (selectedSchool?.uuid) {
      // Reset all projection-specific state when school changes
      setEnrollmentData(null);
      setError(null);
      setTimeframe('5-year');
      setView('school-level');
      setSelectedGrades(['total']);
      setAvailableGrades(['total']);
      setOpenDataDialog(false);
      setMissingDataTypes({ enrollment: false, capacity: false });
      setHasSkippedEnrollmentEntry(false);
      setAdjustedProjections(null);
      setEnrollmentTargets({});
      setSelectedScenarios(['low', 'median', 'high']);
      setShowSuccess(false);
      
      // Check if this school is currently having AI summaries regenerated
      const isThisSchoolRegenerating = regeneratingSchoolsRef.current.has(selectedSchool.uuid);
      setRegeneratingAI(isThisSchoolRegenerating);
      
      hasShownDialogRef.current = false;
      setContentLoading(false);
      setProjectionsLoading(false);
      
      // Reset section loading states
      setSectionLoadingStates({
        risk: { loading: true, dataReady: false },
        impact: { loading: true, dataReady: false },
        actions: { loading: false, dataReady: true }
      });
      
      const schoolUuid = selectedSchool.uuid;
      if (activeSchoolUuidRef.current !== schoolUuid) {
        activeSchoolUuidRef.current = schoolUuid;
        setEnrollmentHistory(null);
        setEnrollmentYearCount(null);
        setForceStandardView(false);
        forceStandardViewRef.current = false;
      }

      // Drop-a-pins have no enrollment data to project; DropAPinProjectionsView loads its own data.
      // Short-history schools start in the hybrid view, which doesn't need projections.
      if (!isDropAPin) {
        loadEnrollmentHistory(schoolUuid).then((count) => {
          if (activeSchoolUuidRef.current !== schoolUuid) return;
          const shortHistory = count != null && count > 0 && count < MIN_YEARS_FOR_PROJECTIONS;
          if (!shortHistory || forceStandardViewRef.current) fetchProjections();
        });
      } else {
        setYearCountLoading(false);
      }
    }
  }, [selectedSchool?.uuid, fetchProjections, isDropAPin, loadEnrollmentHistory]);

  const handleShowStandardView = () => {
    setForceStandardView(true);
    if (!enrollmentData) fetchProjections();
  };

  // Handlers
  const handleSchoolChange = (event) => {
    const schoolId = event.target.value;
    const school = schools.find(s => s.uuid === schoolId);
    if (school) {
      // Update global selected school state (shared across explorers)
      // The useEffect above will handle resetting local state and fetching data
      useSchoolStore.getState().selectSchool({
        ...school,
        name: formatSchoolName(school.name)
      });
    }
  };

  const handleGradeChange = (event) => {
    const value = event.target.value;
    if (view === 'school-level') {
      setSelectedGrades(['total']);
    } else {
      const newSelection = Array.isArray(value) ? value : [value];
      if (newSelection.length === 0) return;
      setSelectedGrades([...newSelection].sort((a, b) => {
        if (a === 'Kindergarten') return -1;
        if (b === 'Kindergarten') return 1;
        return parseInt(a) - parseInt(b);
      }));
    }
  };

  const handleViewChange = (event, newView) => {
    if (newView) {
      setView(newView);
      if (newView === 'grade-level' && (!selectedGrades.length || selectedGrades[0] === 'total')) {
        const validGrades = availableGrades.filter(grade => grade !== 'total');
        if (validGrades.length > 0) {
          setSelectedGrades([validGrades[0]]);
        }
      } else if (newView === 'school-level' && !selectedGrades.includes('total')) {
        setSelectedGrades(['total']);
      }
    }
  };

  const handleAccordionChange = (section) => (event, isExpanded) => {
    // Prevent expanding if data isn't ready
    if (isExpanded && !sectionLoadingStates[section]?.dataReady) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    
    handleExpandedChange(section)(event, isExpanded);
    if (isExpanded) {
      setToolAndSection('projections-explorer', section);
    } else if (!Object.values(expandedSections).some(expanded => expanded)) {
      setToolAndSection('projections-explorer', null);
    }
  };



  const handleSkipDataEntry = () => {
    // Only set hasSkippedEnrollmentEntry if enrollment data was actually missing
    if (missingDataTypes.enrollment) {
      setHasSkippedEnrollmentEntry(true);
    }
    setOpenDataDialog(false);
    hasShownDialogRef.current = true;  // Mark dialog as shown
  };

  const handleScenariosChange = (newScenarios) => {
    setSelectedScenarios(newScenarios);
  };

  const handleProjectionsUpdate = useCallback((adjusted) => {
    setAdjustedProjections(adjusted || null);
  }, []);

  // Render methods
  const renderWarningElement = () => {
    if (!enrollmentData?.based_on_user_data && hasSkippedEnrollmentEntry && canEditData()) {
      return (
        <Alert 
          severity="info" 
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setEnrollmentModalOpen(true)}
            >
              Manage Data
            </Button>
          }
          sx={{ 
            width: '100%',
            '& .MuiAlert-message': {
              padding: '4px 0'
            },
            mb: 2
          }}
        >
          <AlertTitle>Missing Latest Enrollment Data</AlertTitle>
          Your projections are based on historical data only. For more accurate projections, please add your latest enrollment data.
        </Alert>
      );
    }
    return null;
  };

  const renderRiskAssessment = () => {
    if (!selectedSchool || !enrollmentData) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <ProjectXTargetsAssessment 
        enrollmentData={enrollmentData}
        timeframe={timeframe}
        selectedGrades={selectedGrades}
        view={view}
        onTargetsChange={setEnrollmentTargets}
        selectedScenarios={selectedScenarios}
        school={selectedSchool} 
        currentUser={user} 
      />
    );
  };

  const renderFilters = () => {
    return (
      <ProjectionsFilters
        schools={schools}
        selectedSchool={selectedSchool}
        timeframe={timeframe}
        view={view}
        selectedGrades={selectedGrades}
        availableGrades={availableGrades}
        selectedScenarios={selectedScenarios}  
        onSchoolChange={handleSchoolChange}
        onTimeframeChange={(e, value) => value && setTimeframe(value)}
        onViewChange={handleViewChange}
        onGradeChange={handleGradeChange}
        onScenariosChange={handleScenariosChange} 
        expandedSections={expandedSections}
        onExpandAll={handleExpandAll}
        onCollapseAll={handleCollapseAll}
        warningElement={
          <Box sx={{ width: '100%' }}>
            {isShortHistory && (
              <Alert
                severity="info"
                sx={{ width: '100%', mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => setForceStandardView(false)}>
                    Switch to planning worksheet
                  </Button>
                }
              >
                <AlertTitle>Limited enrollment history</AlertTitle>
                This school has {enrollmentYearCount} year{enrollmentYearCount !== 1 ? 's' : ''} of enrollment data.
                Projections based on fewer than {MIN_YEARS_FOR_PROJECTIONS} years are less reliable; the planning
                worksheet may be a better fit.
              </Alert>
            )}
            {renderWarningElement()}
            {selectedSchool && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setEnrollmentModalOpen(true)}
                  startIcon={<EditIcon />}
                  size="small"
                  disabled={!selectedSchool}
                >
                  Manage Enrollment Data
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setCapacityModalOpen(true)}
                  startIcon={<AssignmentIcon />}
                  size="small"
                  disabled={!selectedSchool}
                >
                  Manage Max Grade Capacity
                </Button>
              </Box>
            )}
          </Box>
        }
      />
    );
  };

  const renderMainDashboard = () => {
    // If any of the required projections data is ready, render the dashboard
    const dashboardDataReady = isSectionDataLoaded('risk');
    
    if (!dashboardDataReady) {
      return <Skeleton variant="rectangular" height={400} />;
    }
  
    return (
      <Box sx={{ mt: 0 }}> 
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          bgcolor: 'background.paper'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
          }}>
            <AutoGraphOutlined sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Enrollment Trends & Projections
            </Typography>
          </Box>
        </Box>
        <Box sx={{ p: 1 }}> 
        <Suspense fallback={<Skeleton variant="rectangular" height={400} />}>
          <ProjectXDashboard 
            school={selectedSchool}
            enrollmentData={{
              ...enrollmentData,
              projections: adjustedProjections || enrollmentData?.projections
            }}
            selectedGrades={selectedGrades}
            timeframe={timeframe}
            view={view}
            targets={enrollmentTargets}
            selectedScenarios={selectedScenarios}
            adjustedProjections={adjustedProjections}
            onEditEnrollment={() => setEnrollmentModalOpen(true)}
            aiSummaryRefreshKey={aiSummaryRefreshKey}
            regeneratingAI={regeneratingAI}
          />
          </Suspense>
        </Box>
      </Box>
    );
  };

  const renderRightAccordions = () => {
    return (
      <AccordionContainer 
          expandedSections={expandedSections}
          onSectionChange={handleAccordionChange}
        >
          <AccordionSection
            id="risk"
          title="Risk Assessment"
          summary="Compare your network's enrollment targets to projection scenarios"
          icon={Assessment}
          disabled={!sectionLoadingStates.risk.dataReady}
          loading={sectionLoadingStates.risk.loading}
        >
          <div>
            <Suspense fallback={<Skeleton variant="rectangular" height={400} />}>
              {sectionLoadingStates.risk.dataReady ? (
                renderRiskAssessment()
              ) : (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    py: 8 
                  }}
                >
                  <CircularProgress size={40} sx={{ mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Loading risk assessment data...
                  </Typography>
                </Box>
              )}
            </Suspense>
          </div>
        </AccordionSection>

        <AccordionSection
          id="impact"
          title="Strategy Impact Analysis"
          summary="Model the impact of different recruitment and retention strategies"
          icon={TrendingUp}
          disabled={!sectionLoadingStates.impact.dataReady}
          loading={sectionLoadingStates.impact.loading}
        >
          <div>
            <Suspense fallback={<Skeleton variant="rectangular" height={400} />}>
              {sectionLoadingStates.impact.dataReady ? (
                <ProjectXStrategyImpact 
                  enrollmentData={enrollmentData}
                  onProjectionsUpdate={handleProjectionsUpdate}
                  timeframe={timeframe}
                  selectedGrades={selectedGrades}
                  view={view}
                  enrollmentTargets={enrollmentTargets}
                  selectedScenarios={selectedScenarios}
                  selectedSchool={selectedSchool}
                />
              ) : (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    py: 8 
                  }}
                >
                  <CircularProgress size={40} sx={{ mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Loading strategy impact data...
                  </Typography>
                </Box>
              )}
            </Suspense>
          </div>
        </AccordionSection>

        <AccordionSection
          id="actions"
          title="Now What?"
          summary="Continue to explore with other enrollment planning tools"
          icon={Explore}
        >
          <div>
            <NowWhatSection 
              sectionIcon={Explore}
              actions={[
                {
                  icon: MapsHomeWork,
                  title: 'Geographic Explorer',
                  description: 'Explore demographic and enrollment trends',
                  path: `/geographic-explorer${selectedSchool ? `?school=${selectedSchool.uuid}` : ''}`
                },
                {
                  icon: DonutSmallOutlined,
                  title: 'Competitive Explorer',
                  description: 'Analyze market share data',
                  path: `/competitive-explorer${selectedSchool ? `?school=${selectedSchool.uuid}` : ''}`
                },
                {
                  icon: PersonPinCircleOutlined,
                  title: 'Student Catchment Explorer',
                  description: 'Connect your student locations to community and competitive insights',
                  path: `/student-catchment-explorer${selectedSchool ? `?school=${selectedSchool.uuid}` : ''}`
                }
              ]}
            />
          </div>
        </AccordionSection>
      </AccordionContainer>
    );
  };

  return (
    <>
      <PageLayout
        title="Projections Explorer"
        description={selectedSchool && (isDropAPin || error === 'DROP_A_PIN_LOCATION')
          ? "Market context and grade-level targets for a drop-a-pin location. Adjust filters to match your competitive set."
          : selectedSchool && showHybrid
          ? "Your historical enrollment alongside market context and grade-level targets. Adjust filters to match your competitive set."
          : selectedSchool
          ? "Refine your analysis using the time frame and grade filters. Click on any headline card to view more detailed information and insights about the selected metrics."
          : "Use this tool to explore enrollment projections and plan future scenarios. Start by selecting a school."
        }
      >
        <LoadingErrorWrapper
          loading={schoolsLoading && schools.length === 0}
          error={['DROP_A_PIN_LOCATION', 'NO_ENROLLMENT_DATA', 'LOCATION_ONLY'].includes(error) ? null : error}
          initialLoading={schools.length === 0}
          hideSteps={true}
        >
          {!selectedSchool ? (
            <InitialSchoolSelector
              schools={schools}
              selectedSchool={selectedSchool}
              onSchoolChange={handleSchoolChange}
            />
          ) : isDropAPin || error === 'DROP_A_PIN_LOCATION' ? (
            <Suspense fallback={<Skeleton variant="rectangular" height={600} />}>
              <DropAPinProjectionsView
                schools={schools}
                onSchoolChange={handleSchoolChange}
              />
            </Suspense>
          ) : yearCountLoading && enrollmentYearCount == null ? (
            <Skeleton variant="rectangular" height={600} />
          ) : showHybrid ? (
            <Suspense fallback={<Skeleton variant="rectangular" height={600} />}>
              <HybridProjectionsView
                schools={schools}
                onSchoolChange={handleSchoolChange}
                enrollmentData={enrollmentHistory?.enrollment_data}
                gradeDisplayMap={enrollmentHistory?.grade_display_map}
                yearCount={enrollmentYearCount}
                onShowStandardView={handleShowStandardView}
                onManageEnrollment={() => setEnrollmentModalOpen(true)}
                onManageCapacity={() => setCapacityModalOpen(true)}
              />
            </Suspense>
          ) : error === 'NO_ENROLLMENT_DATA' ? (
            <NoEnrollmentDataMessage />
          ) : error === 'LOCATION_ONLY' ? (
            <LocationOnlyMessage />
          ) : (
            <Suspense fallback={<Skeleton variant="rectangular" height={600} />}>
              <LoadingErrorWrapper
                loading={contentLoading}
                error={error}
                initialLoading={!enrollmentData && selectedSchool}
                hideSteps={true}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {renderFilters()}
                  
                  {/* AI Summary Accordion - Executive Summary (full width) */}
                  <AISummaryAccordion
                    key={`ai-summary-${aiSummaryRefreshKey}`}
                    selectedSchool={selectedSchool}
                    driveTime={10}
                    summaryType="projections"
                    defaultExpanded={true}
                    isRegenerating={regeneratingAI}
                  />
                  
                  <SplitLayout
                    leftContent={renderMainDashboard()}
                    rightContent={renderRightAccordions()}
                  />
                </Box>
              </LoadingErrorWrapper>
            </Suspense>
          )}
        </LoadingErrorWrapper>
      </PageLayout>
  
      {/* Initial data entry dialog */}
      <Dialog
        open={openDataDialog}
        onClose={() => setOpenDataDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {missingDataTypes.enrollment && missingDataTypes.capacity
            ? 'Complete Your School Data'
            : missingDataTypes.enrollment
            ? 'Add Current Enrollment Data'
            : 'Set Grade Capacity Limits'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {missingDataTypes.enrollment && missingDataTypes.capacity ? (
              <>
                To ensure the most accurate enrollment projections, we recommend completing your school's data profile:
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  <li>Add your most recent enrollment data for more accurate projections</li>
                  <li>Set maximum grade capacity limits to cap projected enrollment at realistic levels</li>
                </Box>
                Would you like to add this data now?
              </>
            ) : missingDataTypes.enrollment ? (
              <>
                To ensure the most accurate enrollment projections, we recommend adding your most recent enrollment data. 
                Our enrollment management tool lets you add current enrollment data and view historical records.
                Would you like to access the enrollment manager now?
              </>
            ) : (
              <>
                Set maximum capacity limits for each grade level to ensure projections don't exceed your school's physical capacity. 
                These limits will be applied across all projection scenarios to provide realistic enrollment forecasts.
                Would you like to set capacity limits now?
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSkipDataEntry} variant="outlined" color="inherit">
            Skip for Now
          </Button>
          {missingDataTypes.enrollment && missingDataTypes.capacity ? (
            <>
              <Button
                onClick={() => {
                  setOpenDataDialog(false);
                  setEnrollmentModalOpen(true);
                }}
                variant="outlined"
                color="primary"
              >
                Add Enrollment Data
              </Button>
              <Button
                onClick={() => {
                  setOpenDataDialog(false);
                  setCapacityModalOpen(true);
                }}
                variant="contained"
                color="primary"
                autoFocus
              >
                Set Grade Capacity
              </Button>
            </>
          ) : missingDataTypes.enrollment ? (
            <Button
              onClick={() => {
                setOpenDataDialog(false);
                setEnrollmentModalOpen(true);
              }}
              variant="contained"
              color="primary"
              autoFocus
            >
              Add Enrollment Data
            </Button>
          ) : (
            <Button
              onClick={() => {
                setOpenDataDialog(false);
                setCapacityModalOpen(true);
              }}
              variant="contained"
              color="primary"
              autoFocus
            >
              Set Capacity Limits
            </Button>
          )}
        </DialogActions>
      </Dialog>
  

  
      {/* Success notification */}
      <Snackbar 
        open={showSuccess} 
        autoHideDuration={4000} 
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)}>
          Enrollment data successfully submitted
        </Alert>
      </Snackbar>

      {/* AI Update notification */}
      <Snackbar 
        open={aiUpdateNotification.open} 
        autoHideDuration={aiUpdateNotification.severity === 'info' ? null : 4000}
        onClose={() => setAiUpdateNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={aiUpdateNotification.severity} 
          onClose={() => setAiUpdateNotification(prev => ({ ...prev, open: false }))}
          sx={{ minWidth: 250 }}
        >
          {aiUpdateNotification.message}
        </Alert>
      </Snackbar>

      {/* Enrollment Management Modal */}
      <Dialog 
        open={enrollmentModalOpen} 
        onClose={() => setEnrollmentModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <AssignmentIcon color="primary" />
              <Typography variant="h6" component="h2">
                Enrollment Data Management
              </Typography>
            </Box>
            <IconButton 
              onClick={() => setEnrollmentModalOpen(false)}
              size="small"
            >
              <CancelIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Suspense fallback={<CircularProgress />}>
            {selectedSchool ? (
              <EnrollmentManagementModal
                selectedSchool={selectedSchool}
                                onEnrollmentChange={async () => {
                  // Track that this school is regenerating AI summaries
                  regeneratingSchoolsRef.current.add(selectedSchool.uuid);
                  setRegeneratingAI(true);
                  
                  // Force regeneration of projections when enrollment data changes
                  setEnrollmentData(null);
                  setAdjustedProjections(null); // Clear any adjusted projections
                  setEnrollmentTargets({}); // Clear any enrollment targets
                  setContentLoading(true);
                  setProjectionsLoading(true);
                  setSectionLoadingStates({
                    risk: { loading: true, dataReady: false },
                    impact: { loading: true, dataReady: false },
                    actions: { loading: false, dataReady: true }
                  });
                  
                  try {
                    // Clear all caches related to this school's projections and enrollment
                    clearApiCache(`projections-v2-${selectedSchool.uuid}`);
                    clearApiCache(`enrollment-management-${selectedSchool.uuid}`);
                    clearApiCache(`enrollment-data-v2-${selectedSchool.uuid}`);
                    
                    console.log('[ProjectionsExplorer] Enrollment data saved, waiting for backend to process...');
                    
                    // Wait longer for backend to fully complete the database transaction
                    // and ensure data is committed and visible to subsequent queries
                    await new Promise(resolve => setTimeout(resolve, 2000));

                    const newYearCount = await loadEnrollmentHistory(selectedSchool.uuid);
                    const stillShortHistory =
                      newYearCount != null && newYearCount > 0 && newYearCount < MIN_YEARS_FOR_PROJECTIONS;
                    if (stillShortHistory && !forceStandardViewRef.current) {
                      regeneratingSchoolsRef.current.delete(selectedSchool.uuid);
                      setRegeneratingAI(false);
                      setProjectionsLoading(false);
                      setContentLoading(false);
                      setEnrollmentModalOpen(false);
                      return;
                    }
                    
                    console.log('[ProjectionsExplorer] Regenerating projections with force=true');
                    
                    // Fetch fresh projections with force regenerate flag
                    // This will trigger backend to regenerate with the new enrollment data
                    const data = await schoolsApi.getEnrollmentProjectionsV2(selectedSchool.uuid, true);
                    trackUsage('projection_run', { schoolUuid: selectedSchool.uuid });
                    
                    const actualYears = Object.keys(data.actual_enrollment || {}).sort();
                    const projectionYears = Object.keys(data.projections?.median || {}).sort();
                    
                    console.log('[ProjectionsExplorer] Received regenerated projections:');
                    console.log('  - Actual enrollment years:', actualYears);
                    console.log('  - Projection years:', projectionYears);
                    console.log('  - Based on user data:', data.based_on_user_data);
                    console.log('  - Latest actual year:', actualYears[actualYears.length - 1]);
                    console.log('  - First projection year:', projectionYears[0]);
                    
                    setEnrollmentData(data);
                    
                    // Update component loading states
                    setSectionLoadingStates(prev => ({
                      ...prev,
                      risk: { loading: false, dataReady: true },
                      impact: { loading: false, dataReady: true }
                    }));
                    setProjectionsLoading(false);
                    setContentLoading(false);
                    
                    // Regenerate AI summaries (projections + overview) in the background
                    try {
                      // Don't show "Updating" notification yet - wait to see if it succeeds or is skipped
                      await regenerateProjectionsSummary(selectedSchool.uuid);
                      // Only show notification if regeneration actually happened (not skipped)
                      console.log('[ProjectionsExplorer] AI summaries regenerated (projections + overview)');
                      setAiUpdateNotification({ open: true, message: 'AI insights updated!', severity: 'success' });
                      // Trigger AI components to refresh with new data
                      setAiSummaryRefreshKey(prev => prev + 1);
                    } catch (aiError) {
                      console.warn('[ProjectionsExplorer] Failed to regenerate AI summaries:', aiError);
                      // Check if regeneration was skipped due to missing summaries
                      if (aiError.message && aiError.message.includes('complete set')) {
                        console.log('[ProjectionsExplorer] AI summary regeneration skipped - summaries not yet generated');
                        // Don't show notification for schools without complete summaries
                      } else {
                        // Show notification for actual failures
                        setAiUpdateNotification({ open: true, message: 'AI insights update failed', severity: 'warning' });
                      }
                    } finally {
                      // Remove school from regenerating set and clear loading indicator
                      regeneratingSchoolsRef.current.delete(selectedSchool.uuid);
                      setRegeneratingAI(false);
                    }
                  } catch (error) {
                    console.error('Error regenerating projections:', error);
                    // Remove school from regenerating set and clear loading indicator on error
                    regeneratingSchoolsRef.current.delete(selectedSchool.uuid);
                    setRegeneratingAI(false);
                    // Still try to fetch projections even if regeneration fails
                    setProjectionsLoading(false);
                    await fetchProjections();
                  }
                  
                  // Close modal after successful save
                  setEnrollmentModalOpen(false);
                }}
                userPermissions={{
                  canEdit: canEditData(),
                  canView: true
                }}
              />
            ) : (
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  py: 8 
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Select a school to manage enrollment data
                </Typography>
              </Box>
            )}
          </Suspense>
        </DialogContent>
      </Dialog>

      {/* Grade Capacity Manager Modal */}
      <Dialog 
        open={capacityModalOpen} 
        onClose={() => setCapacityModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            minHeight: '70vh',
            maxHeight: '85vh'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <AssignmentIcon color="primary" />
              <Typography variant="h6" component="h2">
                Grade Capacity Management
              </Typography>
            </Box>
            <IconButton 
              onClick={() => setCapacityModalOpen(false)}
              size="small"
            >
              <CancelIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'auto', height: '100%' }}>
          <Suspense fallback={<CircularProgress />}>
            {selectedSchool ? (
              <GradeCapacityManager
                selectedSchool={selectedSchool}
                onCapacityChange={async () => {
                  // Track that this school is regenerating AI summaries
                  regeneratingSchoolsRef.current.add(selectedSchool.uuid);
                  setRegeneratingAI(true);
                  
                  // Regenerate projections with new capacities
                  setEnrollmentData(null);
                  setAdjustedProjections(null); // Clear any adjusted projections
                  setEnrollmentTargets({}); // Clear any enrollment targets
                  setContentLoading(true);
                  setProjectionsLoading(true);
                  setSectionLoadingStates({
                    risk: { loading: true, dataReady: false },
                    impact: { loading: true, dataReady: false },
                    actions: { loading: false, dataReady: true }
                  });
                  
                  try {
                    // Clear all caches related to this school's projections and capacity
                    clearApiCache(`projections-v2-${selectedSchool.uuid}`);
                    clearApiCache(`capacities-${selectedSchool.uuid}`);
                    
                    console.log('[ProjectionsExplorer] Capacity data saved, waiting for backend to process...');
                    
                    // Wait longer for backend to fully complete the database transaction
                    // and ensure data is committed and visible to subsequent queries
                    await new Promise(resolve => setTimeout(resolve, 2000));
                    
                    console.log('[ProjectionsExplorer] Regenerating projections with capacity constraints and force=true');
                    
                    // Fetch fresh projections with force regenerate flag
                    // This will trigger backend to regenerate with the new capacity data
                    const data = await schoolsApi.getEnrollmentProjectionsV2(selectedSchool.uuid, true);
                    trackUsage('projection_run', { schoolUuid: selectedSchool.uuid });
                    
                    const actualYears = Object.keys(data.actual_enrollment || {}).sort();
                    const projectionYears = Object.keys(data.projections?.median || {}).sort();
                    
                    console.log('[ProjectionsExplorer] Received regenerated projections with capacities:');
                    console.log('  - Actual enrollment years:', actualYears);
                    console.log('  - Projection years:', projectionYears);
                    console.log('  - Latest actual year:', actualYears[actualYears.length - 1]);
                    console.log('  - First projection year:', projectionYears[0]);
                    
                    setEnrollmentData(data);
                    
                    // Update component loading states
                    setSectionLoadingStates(prev => ({
                      ...prev,
                      risk: { loading: false, dataReady: true },
                      impact: { loading: false, dataReady: true }
                    }));
                    setProjectionsLoading(false);
                    setContentLoading(false);
                    
                    // Regenerate AI summaries (projections + overview) in the background
                    try {
                      // Don't show "Updating" notification yet - wait to see if it succeeds or is skipped
                      await regenerateProjectionsSummary(selectedSchool.uuid);
                      // Only show notification if regeneration actually happened (not skipped)
                      console.log('[ProjectionsExplorer] AI summaries regenerated (projections + overview)');
                      setAiUpdateNotification({ open: true, message: 'AI insights updated!', severity: 'success' });
                      // Trigger AI components to refresh with new data
                      setAiSummaryRefreshKey(prev => prev + 1);
                    } catch (aiError) {
                      console.warn('[ProjectionsExplorer] Failed to regenerate AI summaries:', aiError);
                      // Check if regeneration was skipped due to missing summaries
                      if (aiError.message && aiError.message.includes('complete set')) {
                        console.log('[ProjectionsExplorer] AI summary regeneration skipped - summaries not yet generated');
                        // Don't show notification for schools without complete summaries
                      } else {
                        // Show notification for actual failures
                        setAiUpdateNotification({ open: true, message: 'AI insights update failed', severity: 'warning' });
                      }
                    } finally {
                      // Remove school from regenerating set and clear loading indicator
                      regeneratingSchoolsRef.current.delete(selectedSchool.uuid);
                      setRegeneratingAI(false);
                    }
                  } catch (error) {
                    console.error('Error regenerating projections:', error);
                    // Remove school from regenerating set and clear loading indicator on error
                    regeneratingSchoolsRef.current.delete(selectedSchool.uuid);
                    setRegeneratingAI(false);
                    if (!showHybrid) setError('Failed to regenerate projections with new capacities');
                    setProjectionsLoading(false);
                    setContentLoading(false);
                  }
                  
                  // Close modal after successful save
                  setCapacityModalOpen(false);
                }}
                userPermissions={{
                  canEdit: canEditData(),
                  canView: true
                }}
              />
            ) : (
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  py: 8 
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Select a school to manage grade capacities
                </Typography>
              </Box>
            )}
          </Suspense>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ProjectionsExplorer;